import { cardOutIcon, outIcon, inIcon } from '@peanuds/ui';
import { formatSignedCurrencyAmount, type TransactionAmountFormatConfig } from '../utils/currency';
import {
  DEFAULT_DATE_LOCALE,
  formatDateOnly,
  formatDateValue,
  isDateOnlyValue,
} from '../utils/datetime';
import type { ControlSpendingTransaction } from '../transactions/control-spendings.types';

export type TransactionTableRow = ControlSpendingTransaction;

const dateTimeFormatOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
};

const transactionDateLocale = DEFAULT_DATE_LOCALE;

const toTrimmedString = (value: unknown): string => {
  return typeof value === 'string' ? value.trim() : '';
};

const fallbackString = (...values: Array<unknown>): string => {
  for (const value of values) {
    const normalized = toTrimmedString(value);
    if (normalized) return normalized;
  }

  return '';
};

const formatGenericPaymentType = (value: unknown): string => {
  const paymentType = toTrimmedString(value);
  if (!paymentType) return '';

  return paymentType
    .split('-')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(' ');
};

const getNestedString = (source: unknown, path: string[]): string => {
  let current: unknown = source;

  for (const key of path) {
    if (!current || typeof current !== 'object') return '';
    current = (current as Record<string, unknown>)[key];
  }

  return toTrimmedString(current);
};

const getOutgoingBeneficiaryName = (row: TransactionTableRow): string => {
  return fallbackString(
    getNestedString(row, ['beneficiary', 'fullName']),
    `${getNestedString(row, ['beneficiary', 'identity', 'givenName'])} ${getNestedString(row, [
      'beneficiary',
      'identity',
      'familyName',
    ])}`,
    getNestedString(row, ['beneficiary', 'identity', 'legalName']),
    getNestedString(row, ['beneficiary', 'account', 'accountHolderName']),
  );
};

const getIncomingCounterpartyName = (row: TransactionTableRow): string => {
  return fallbackString(
    getNestedString(row, ['senderName']),
    getNestedString(row, ['senderAccount', 'accountHolderName']),
    getNestedString(row, ['senderAccount', 'alias']),
    getNestedString(row, ['senderInformation']),
    getNestedString(row, ['senderAccount', 'iban']),
    getNestedString(row, ['senderAccount', 'accountNumber']),
  );
};

const getGenericTransactionFallback = (row: TransactionTableRow): string => {
  if (row?.transactionSource !== 'GENERIC_TRANSFER') return '';

  return formatGenericPaymentType(row.genericTransferType);
};

export function getTransactionName(row: TransactionTableRow): string {
  const direction = (row?.direction ?? '').toString().toLowerCase();

  if (direction === 'out') {
    return fallbackString(getOutgoingBeneficiaryName(row), getGenericTransactionFallback(row));
  }

  return fallbackString(getIncomingCounterpartyName(row), getGenericTransactionFallback(row));
}

export function getTransactionIban(row: TransactionTableRow): string {
  const direction = (row?.direction ?? '').toString().toLowerCase();

  if (direction === 'out') {
    return fallbackString(
      getNestedString(row, ['beneficiary', 'account', 'iban']),
      getNestedString(row, ['beneficiary', 'account', 'accountNumber']),
    );
  }

  return fallbackString(
    getNestedString(row, ['senderAccount', 'iban']),
    getNestedString(row, ['senderAccount', 'accountNumber']),
  );
}

export function getTransactionReference(row: TransactionTableRow): string {
  return fallbackString(
    getNestedString(row, ['reference']),
    getNestedString(row, ['senderInformation']),
    getNestedString(row, ['narrative']),
    getNestedString(row, ['description']),
    getNestedString(row, ['serviceProviderReference']),
    getNestedString(row, ['transactionNumber']),
    getNestedString(row, ['genericTransferType']),
  );
}

export function getTransactionReferenceDetails(row: TransactionTableRow): string {
  return fallbackString(
    getNestedString(row, ['reference']),
    getNestedString(row, ['description']),
    getNestedString(row, ['senderInformation']),
    getNestedString(row, ['serviceProviderReference']),
  );
}

export function getTransactionUserName(row: TransactionTableRow): string {
  const initiatorFirstName = getNestedString(row, ['initiatedBy', 'firstName']);
  const initiatorLastName = getNestedString(row, ['initiatedBy', 'lastName']);
  const givenName = getNestedString(row, ['owner', 'givenName']);
  const familyName = getNestedString(row, ['owner', 'familyName']);

  return fallbackString(
    `${initiatorFirstName} ${initiatorLastName}`,
    getNestedString(row, ['owner', 'fullName']),
    `${givenName} ${familyName}`,
  );
}

export function getTransactionDisplayName(row: TransactionTableRow): string {
  return getTransactionName(row) || '-';
}

export function getTransactionDisplayIban(row: TransactionTableRow): string {
  return getTransactionIban(row) || '-';
}

export function getTransactionDisplayReference(row: TransactionTableRow): string {
  return getTransactionReference(row) || '-';
}

export function getTransactionDisplayUserName(row: TransactionTableRow): string {
  return getTransactionUserName(row) || '-';
}

export const getRowDateCandidate = (row: TransactionTableRow): string => {
  return fallbackString(
    row?.dateAndTime,
    row?.transferDate,
    row?.transactionTimestampInitial,
    row?.createdAt,
  );
};

export function formatRowDate(row: TransactionTableRow): string {
  const candidate = getRowDateCandidate(row);

  if (!candidate) return '-';

  if (isDateOnlyValue(candidate)) {
    return formatDateOnly(candidate, transactionDateLocale) ?? '-';
  }

  return formatDateValue(candidate, dateTimeFormatOptions, transactionDateLocale) ?? '-';
}

export function formatRowDateOnly(row: TransactionTableRow): string {
  const candidate = getRowDateCandidate(row);

  if (!candidate) return '-';

  const dateOnlyCandidate = candidate.slice(0, 10);

  if (!isDateOnlyValue(dateOnlyCandidate)) return '-';

  return formatDateOnly(dateOnlyCandidate, transactionDateLocale) ?? '-';
}

export function getNumericAmount(row: TransactionTableRow): number | null {
  // Card transaction (completed)
  const cardCompleted = row?.completedDetails?.sourceAmount;
  if (cardCompleted && typeof cardCompleted.amount === 'number') {
    return cardCompleted.amount as number;
  }
  // Card transaction (simpleAmount fallback)
  const simpleSource = row?.simpleAmount?.sourceAmount;
  if (simpleSource && typeof simpleSource.amount === 'number') {
    return simpleSource.amount as number;
  }
  // Bank/fee transactions with string amount
  if (row?.amount !== undefined && row?.amount !== null) {
    const n = typeof row.amount === 'number' ? row.amount : Number(row.amount);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

export function getCurrency(row: TransactionTableRow): string {
  return (
    row?.completedDetails?.sourceAmount?.currency ||
    row?.simpleAmount?.sourceAmount?.currency ||
    row?.currency ||
    'EUR'
  );
}

export function formatAmount(row: TransactionTableRow): string {
  const value = getNumericAmount(row);
  if (value === null) return '-';

  const currency = getCurrency(row);
  try {
    return formatSignedCurrencyAmount(value, currency, row?.direction?.toString());
  } catch {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}`;
  }
}

export function formatAmountWithCustomerConfig(
  row: TransactionTableRow,
  config?: TransactionAmountFormatConfig,
): string {
  const value = getNumericAmount(row);
  if (value === null) return '-';

  const currency = getCurrency(row);
  try {
    return formatSignedCurrencyAmount(value, currency, row?.direction?.toString(), config);
  } catch {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}`;
  }
}

export function isFailedStatus(row: TransactionTableRow): boolean {
  const raw = (row?.state ?? row?.status ?? '').toString();
  const normalized = raw.trim().toUpperCase();
  return normalized === 'FAILED';
}

export type TransactionStatusTag = {
  value: string;
  severity: 'success' | 'danger' | 'info' | 'warn';
};

export const mapTransactionStatusToTag = (status: unknown): TransactionStatusTag => {
  const raw = (status ?? '').toString();
  const normalized = raw.trim().toUpperCase();

  if (normalized === 'COMPLETED' || normalized === 'COMPLETED_SETTLED') {
    return { value: 'analytics.transactions.status_tags.completed', severity: 'success' };
  }
  if (normalized === 'FAILED') {
    return { value: 'analytics.transactions.status_tags.failed', severity: 'danger' };
  }
  if (normalized === 'PENDING') {
    return { value: 'analytics.transactions.status_tags.pending', severity: 'warn' };
  }
  if (normalized === 'ON-HOLD') {
    return { value: 'analytics.transactions.status_tags.on_hold', severity: 'warn' };
  }
  if (normalized === 'IN_REVIEW') {
    return { value: 'analytics.transactions.status_tags.in_review', severity: 'info' };
  }
  if (normalized === 'RELEASED') {
    return { value: 'analytics.transactions.status_tags.processing', severity: 'info' };
  }
  if (normalized === 'PROCESSING') {
    return { value: 'analytics.transactions.status_tags.processing', severity: 'info' };
  }
  if (normalized === 'CANCELLED') {
    return { value: 'analytics.transactions.status_tags.cancelled', severity: 'danger' };
  }

  return {
    value: normalized ? raw.trim() : 'analytics.transactions.status_tags.other',
    severity: 'warn',
  };
};

export function mapStatusToTag(row: TransactionTableRow): TransactionStatusTag {
  return mapTransactionStatusToTag(row?.state ?? row?.status);
}

export function mapNameToIconAndLabel(row: TransactionTableRow): {
  icon: string;
  label: string;
} {
  if (row?.type === 'OUTGOING_PAYMENT') {
    return {
      icon: cardOutIcon,
      label: getTransactionDisplayName(row),
    };
  }
  // Bank transfers
  const dir = (row?.direction ?? '').toString().toLowerCase();

  const icon = dir === 'out' ? outIcon : dir === 'in' ? inIcon : '';

  const label = getTransactionDisplayName(row);

  return { icon, label };
}
